import { BufferGeometry, BufferAttribute, Sphere, Vector3 } from 'three';

/**
 * Rovná mřížka chunku v lokálních souřadnicích 0..size, plus "sukně" po obvodu.
 *
 * Výška se dopočítává až ve vertex shaderu z výškové mapy, takže tahle geometrie
 * je pro všechny chunky stejná — vzniká jednou na LOD úroveň (celkem 5 bufferů)
 * a všech 256 chunků z nich jen vybírá. Přepnutí LOD je pak jen záměna reference.
 *
 * Sukně (svislý lem tažený dolů) zakrývá praskliny na hranici dvou různých LODů,
 * kde hrubší mřížka mine detail výškové mapy.
 */
export function buildChunkGeometry(segments: number, size: number): BufferGeometry {
  const n = segments + 1;
  const step = size / segments;
  const gridCount = n * n;
  const vertCount = gridCount + 4 * n;

  const positions = new Float32Array(vertCount * 3);
  const normals = new Float32Array(vertCount * 3);
  // Hloubka sukně jako per-vertex atribut: hrubší LOD potřebuje hlubší lem.
  const skirtDepth = Math.min(120, 4 * step + 8);
  const skirt = new Float32Array(vertCount);

  for (let iz = 0; iz < n; iz++) {
    for (let ix = 0; ix < n; ix++) {
      const i = iz * n + ix;
      positions[i * 3] = ix * step;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = iz * step;
      normals[i * 3 + 1] = 1;
    }
  }

  // Čtyři okraje: seznam indexů mřížky + směr ven z chunku (pro správné vinutí).
  const edges: { grid: number[]; outward: [number, number] }[] = [
    { grid: [], outward: [0, -1] },
    { grid: [], outward: [0, 1] },
    { grid: [], outward: [-1, 0] },
    { grid: [], outward: [1, 0] },
  ];
  for (let i = 0; i < n; i++) {
    edges[0].grid.push(i);
    edges[1].grid.push((n - 1) * n + i);
    edges[2].grid.push(i * n);
    edges[3].grid.push(i * n + (n - 1));
  }

  const indices: number[] = [];
  for (let iz = 0; iz < segments; iz++) {
    for (let ix = 0; ix < segments; ix++) {
      const a = iz * n + ix;
      const b = a + 1;
      const c = a + n;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }

  const ab = new Vector3();
  const ac = new Vector3();
  const nrm = new Vector3();

  edges.forEach((edge, k) => {
    const base = gridCount + k * n;
    for (let i = 0; i < n; i++) {
      const g = edge.grid[i];
      const s = base + i;
      positions[s * 3] = positions[g * 3];
      positions[s * 3 + 1] = 0;
      positions[s * 3 + 2] = positions[g * 3 + 2];
      normals[s * 3 + 1] = 1;
      skirt[s] = skirtDepth;
    }

    // Vinutí odvodíme z prvního trojúhelníku, ať lem není odříznutý backface cullingem.
    const p0 = edge.grid[0];
    const p1 = edge.grid[1];
    const s0 = base;
    ab.set(positions[s0 * 3] - positions[p0 * 3], -skirtDepth, positions[s0 * 3 + 2] - positions[p0 * 3 + 2]);
    ac.set(positions[p1 * 3] - positions[p0 * 3], 0, positions[p1 * 3 + 2] - positions[p0 * 3 + 2]);
    nrm.crossVectors(ab, ac);
    const flip = nrm.x * edge.outward[0] + nrm.z * edge.outward[1] < 0;

    for (let i = 0; i < segments; i++) {
      const g0 = edge.grid[i];
      const g1 = edge.grid[i + 1];
      const k0 = base + i;
      const k1 = base + i + 1;
      if (flip) {
        indices.push(g0, g1, k0, g1, k1, k0);
      } else {
        indices.push(g0, k0, g1, g1, k0, k1);
      }
    }
  });

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(normals, 3));
  geometry.setAttribute('aSkirt', new BufferAttribute(skirt, 1));
  geometry.setIndex(vertCount > 65535 ? Array.from(indices) : new BufferAttribute(new Uint16Array(indices), 1));

  // Frustum culling řešíme sami v Terrain.ts (sdílená geometrie nezná výšky konkrétního
  // chunku), ale bounding sphere musí existovat, aby three neházelo NaN.
  geometry.boundingSphere = new Sphere(new Vector3(size * 0.5, 0, size * 0.5), size);

  return geometry;
}
